import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Customer } from '@usf/customer-types';
import {
  IMPORT_ORDERS_ACTIONS,
  ImportOrderCheck,
  ImportOrdersStoreService,
  MyOrdersStoreService,
  Order,
  OrderStatus,
  ImportedOrdersFilters,
} from '@usf/ngrx-order';
import {
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  map,
  skip,
  switchMap,
  take,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Crumb } from 'src/app/shared/components/breadcrumbs/breadcrumbs.component';
import { PageTitle } from 'src/app/shared/constants/page-titles.enum';
import {
  ImportedOrdersQuickFilters,
  ImportedOrdersViewModel,
} from 'src/app/shared/models/my-orders-view.model';
import { OrderService } from '../../services/order.service';
import { ModalWithNavComponent } from '../../../shared/components/modal-with-nav/modal-with-nav.component';
import { OrderStatusDetails } from '../../../shared/constants/order-status-title.enum';
import { PATHS } from '../../../shared/constants/paths';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { HelpModalDesktopComponent } from '../../../shared/components/help-modal/help-modal-desktop/help-modal-desktop.component';
import { HelpModalTouchComponent } from '../../../shared/components/help-modal/help-modal-touch/help-modal-touch.component';
import { QuickFilter } from '../../../shared/components/usf-filtering-banner/model/quick-filter.model';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { OrderTransaction } from '@usf/order-types';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { CalendarV2ModalComponent } from '@shared/components/calendar-v2-modal/calendar-v2-modal.component';
import { Observable, Subscription, combineLatest, interval, of } from 'rxjs';
import { ImportedOrdersService } from '../../services/imported-orders.service';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { Message } from '@app/ngrx-message/models/client/message';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { ORDER_ACTIONS } from '@order/actions/order-actions';
import { FEATURES } from '@shared/constants/splitio-features';
import { DownloadFileTypes } from '@shared/constants/order-download-file';
import { ImportedOrdersAnalyticsService } from './tracking/analytics/imported-orders-analytics.service';
import { ImportedOrdersTracingService } from './tracking/tracing/imported-orders-tracing.service';
import { DatePipe } from '@angular/common';
import { DownloadOrdersEvent } from '@order/models/download-orders-event';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-imported-orders',
  templateUrl: './imported-orders.page.html',
  styleUrls: ['./imported-orders.page.scss'],
})
export class ImportedOrdersPage implements OnInit {
  static IMPORTED_ORDERS_API_RETRY_INTERVAL = 10_000; //20 seconds
  static MAX_IMPORTED_ORDERS_REFRESH_CALLS = 2;

  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  progress = 0;
  refreshDataCount = 0;

  breadcrumbs: Crumb[];
  customers: Customer[] = [];
  ordersListSubscription$: Subscription;
  myOrders: ImportedOrdersViewModel;
  modalOpen = false;
  numberOfSelectedFilters = 0;
  formObject: UntypedFormGroup;
  @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;
  orderStatusEnum = OrderStatus;
  importedDateRange: string;
  numberOfOrders: number;
  checkNumberOrders: number;
  importOrderIdentifier: string;
  viewTodaysOrdersClicked = false;

  importOrderModalIsOpen$ = new BehaviorSubject<boolean>(false);
  importOrdersLoading$: Observable<boolean> =
    this.importOrdersStoreService.loading$();
  importOrdersLoaded$: Observable<boolean> =
    this.importOrdersStoreService.loaded$();
  importOrdersTransactions$: Observable<OrderTransaction[]> =
    this.importOrdersStoreService.orderTransactions$();
  fileImportErrors$: Observable<string[]> =
    this.importOrdersStoreService.fileImportErrors$();
  fileImportMessage$: Observable<string> =
    this.importOrdersStoreService.message$();
  apiError$: Observable<string> = this.importOrdersStoreService.error$();

  downloadImportedOrdersModalIsOpen$ = new BehaviorSubject<boolean>(false);
  downloadImportedOrdersFileTypes = [DownloadFileTypes.CSV];

  importedOrdersActionSheetIsOpen$ = new BehaviorSubject<boolean>(false);

  actionSheetButtonsWithoutDownload = [
    {
      text: "View Today's Orders",
      handler: () => this.getTodaysOrders(),
    },
    {
      text: 'Select Different Date',
      handler: () => this.openCalendarModal(),
    },
    { text: 'Cancel', role: 'cancel', cssClass: 'cancel-button' },
  ];
  actionSheetButtons = [
    {
      text: 'Download',
      handler: () => this.showDownloadImportedOrdersModal(true),
    },
    ...this.actionSheetButtonsWithoutDownload,
  ];

  importedOrders$: Observable<Order[]>;
  importOrderCheck$: Observable<ImportOrderCheck>;
  progressBarValue$: Observable<number>;
  importOrderAPIInterval$: Subscription;

  constructor(
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    private myOrdersStoreService: MyOrdersStoreService,
    private importOrdersStoreService: ImportOrdersStoreService,
    private navigationHelper: NavigationHelperService,
    private store: Store,
    private orderService: OrderService,
    private modalController: ModalController,
    private formBuilder: FormBuilder,
    private datePipe: DatePipe,
    private cdRef: ChangeDetectorRef,
    private modalService: ModalService,
    private importedOrdersService: ImportedOrdersService,
    private importedOrdersAnalyticsService: ImportedOrdersAnalyticsService,
    private importedOrdersTracingService: ImportedOrdersTracingService,
    private messageStoreService: MessageStoreService,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.importedOrders',
        url: '',
        active: true,
      },
    ];
  }

  reloadList(vm?: ImportedOrdersViewModel): void {
    if (this.viewPort) {
      this.myOrders = !!vm ? null : this.myOrders;
      setTimeout(() => {
        this.myOrders = !!vm ? { ...vm } : this.myOrders;
        this.cdRef.detectChanges();
      });
    }
  }

  ionViewWillEnter() {
    this.importedDateRange = `Imported `;
    this.importOrderCheck$ = this.importOrdersStoreService.importOrderCheck$();
    this.viewTodaysOrdersClicked = false;

    this.importOrderCheck$
      .pipe(take(1))
      .subscribe(
        check =>
          (this.importOrderIdentifier = check?.importOrderIdentifier ?? ''),
      );

    this.importedOrders$ = this.importOrderCheck$.pipe(
      switchMap(check => {
        if (!!check && !this.myOrders?.filters?.filters?.showAllOrdersForDate) {
          return this.importedOrdersService.importedOrdersById$(
            check?.importOrderIdentifier,
          );
        } else {
          const today = new Date().toLocaleDateString('en-US');
          return this.importedOrdersService.importedOrdersByDate$(
            this.myOrders?.filters?.filters?.importedDate ?? today,
          );
        }
      }),
    );

    this.progressBarValue$ = combineLatest([
      this.importedOrders$,
      this.importOrderCheck$,
    ]).pipe(
      distinctUntilChanged((prev, curr) => prev[0]?.length === curr[0]?.length),
      takeWhile(([_orders, check]) => !!check && !check.ordersLoaded),
      map(([orders, check]) => {
        const received = orders?.length ?? 0;
        const expected = check?.orderCount ?? 0;
        const complete = received / expected;
        if (complete === 1 && !!check) {
          const message = {
            type: MessageTypeEnum.success,
            display: 'Your imported orders have processed.',
            toast: true,
            sticky: false,
          } as Message;
          this.messageStoreService.upsertMessage(message);
          this.importOrdersStoreService.setImportOrderCheck({
            ...check,
            ordersLoaded: true,
          });
        }
        return complete && !Number.isNaN(complete) ? complete : 0;
      }),
      defaultIfEmpty(1),
    );

    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.importedOrders),
    );

    this.reloadList();
    // first event emitted create the form, load filters and update orders
    this.orderService
      .loadImportedOrdersViewModel()
      .pipe(
        withLatestFrom(this.panAppState.user$),
        filter(([x, user]) => !!user),
        map(([x, user]) => x),
        take(2),
      )
      .subscribe(x => {
        this.myOrders = x;
        if (
          !!this.importOrderIdentifier &&
          !!x.filters?.filters?.importedDate
        ) {
          this.getImportedOrdersById(this.importOrderIdentifier);
        } else if (
          !this.importOrderIdentifier &&
          !!x.filters?.filters?.importedDate
        ) {
          const importDate = new Date(
            x.filters.filters.importedDate,
          ).toLocaleDateString('en-CA');
          this.getImportedOrdersByDate(importDate);
        }

        if (!this.formObject || (x.filters && this.importOrderIdentifier)) {
          this.createForm(x.defaultDateFilter);
          this.numberOfSelectedFilters = 1;
        } else if (x.filters && x.filters.filtersSelected) {
          if (!this.formObject) {
            this.createForm();
          }

          const importOrdersFilters: ImportedOrdersFilters = {
            importedDate: x.filters.filters.importedDate,
            orderStatus: undefined,
            showAllOrdersForDate: x.filters.filters.showAllOrdersForDate,
          };
          Object.keys(x.filters.filters.orderStatus).forEach(key => {
            if (this.formObject.get('orderStatus').get(key)) {
              importOrdersFilters.orderStatus = {
                ...importOrdersFilters.orderStatus,
                [key]: x.filters.filters.orderStatus[key],
              };
            }
          });

          this.formObject.setValue({
            ...importOrdersFilters,
            showAllOrdersForDate: false,
          });
          this.numberOfSelectedFilters = x.filters.filtersSelected;
        }
      });

    // after first event emitted only update orders
    this.ordersListSubscription$ = this.orderService
      .loadImportedOrdersViewModel()
      .pipe(
        withLatestFrom(this.panAppState.user$),
        filter(([x, user]) => !!user),
        map(([x, user]) => x),
        skip(1),
      )
      .subscribe(vm => {
        this.myOrders = vm;
        this.reloadList(vm);
        this.importedDateRange = `Imported ${vm.filters.filters.importedDate}`;
      });

    this.importedOrdersService.pollImportedOrdersById(
      this.importOrderIdentifier,
    );
  }

  ionViewDidEnter() {
    this.importedOrdersTracingService.traceImportedOrdersPageLoad();
    this.importedOrdersAnalyticsService.trackImportedOrderPageLoad();
  }

  ionViewWillLeave() {
    if (this.ordersListSubscription$) {
      this.ordersListSubscription$.unsubscribe();
    }
    if (this.importOrderAPIInterval$) {
      this.importOrderAPIInterval$.unsubscribe();
    }
    this.toggleShowTodaysOrders(false);
  }

  quickFitlerCanBeActivated(filters: OrderStatus[]): boolean {
    let quickFitlersCanBeActivated = false;
    const allFiltersSelected = filters.every(
      statusKey => this.formObject?.value?.orderStatus[statusKey],
    );
    if (!allFiltersSelected) {
      quickFitlersCanBeActivated = true;
    } else {
      Object.keys(this.formObject.value.orderStatus).every(key => {
        if (!filters.includes(OrderStatus[key])) {
          if (this.formObject.value.orderStatus[key]) {
            quickFitlersCanBeActivated = true;
            return false;
          }
        }
        return true;
      });
    }

    return quickFitlersCanBeActivated;
  }

  deactivateStatusFilters(filters: OrderStatus[]) {
    let newFormObjectValue = { ...this.formObject.value };
    filters.forEach(filter => {
      newFormObjectValue = {
        ...newFormObjectValue,
        orderStatus: {
          ...newFormObjectValue.orderStatus,
          [filter]: false,
        },
      };
    });
    return newFormObjectValue;
  }

  activateStatusFilters(filters: OrderStatus[]) {
    let newOrderStatusFilters = new Map<OrderStatus, boolean>();
    Object.keys(this.formObject.value.orderStatus).forEach(statusKey => {
      if (filters.includes(OrderStatus[statusKey])) {
        newOrderStatusFilters[statusKey] = true;
      } else {
        newOrderStatusFilters[statusKey] = false;
      }
    });
    const newFormObjectValue = {
      ...this.formObject.value,
      orderStatus: newOrderStatusFilters,
    };
    return newFormObjectValue;
  }

  toggleStatusFilter(quickFilter: QuickFilter) {
    let newFormObjectValue;
    if (quickFilter.id === ImportedOrdersQuickFilters.ALL_IMPORTED_ORDERS) {
      if (this.quickFitlerCanBeActivated([])) {
        newFormObjectValue = this.activateStatusFilters([]);
      } else {
        newFormObjectValue = this.deactivateStatusFilters([]);
      }
    } else if (quickFilter.id === ImportedOrdersQuickFilters.SUBMITTED) {
      if (this.quickFitlerCanBeActivated([OrderStatus.SUBMITTED])) {
        newFormObjectValue = this.activateStatusFilters([
          OrderStatus.SUBMITTED,
        ]);
      } else {
        newFormObjectValue = this.deactivateStatusFilters([
          OrderStatus.SUBMITTED,
        ]);
      }
    } else {
      if (this.quickFitlerCanBeActivated([OrderStatus[quickFilter.id]])) {
        newFormObjectValue = this.activateStatusFilters([quickFilter.id]);
      } else {
        newFormObjectValue = this.deactivateStatusFilters([quickFilter.id]);
      }
    }

    this.formObject.setValue(newFormObjectValue);
    this.applyFilters(
      { data: { numberOfSelectedFilters: this.numberOfSelectedFilters } },
      newFormObjectValue,
    );
  }

  statusFilters(): Map<OrderStatus, string> {
    const filterStatusText: Map<OrderStatus, string> = new Map();
    Object.keys(OrderStatus).forEach(status => {
      const text = OrderStatusDetails[status]?.filterText;
      if (!!text) {
        filterStatusText.set(
          OrderStatus[status],
          this.translateService.instant(OrderStatusDetails[status]?.filterText),
        );
      }
    });
    return filterStatusText;
  }

  // check if any filter is selected... maybe the modal could return a variable that track that. I would prevent the looping into
  applyFilters(resp, formValue) {
    if (resp.data) {
      this.numberOfSelectedFilters = resp.data.numberOfSelectedFilters;
      this.myOrdersStoreService.setImportedOrdersFilters(
        { ...this.formObject.value } as ImportedOrdersFilters,
        this.numberOfSelectedFilters,
      );
    } else {
      this.formObject.setValue(formValue);
    }
    this.modalOpen = false;
  }

  createForm(dateFilter: any = null) {
    const importedDate = dateFilter?.importedDate
      ? dateFilter.importedDate
      : '';
    this.formObject = this.formBuilder.group({
      orderStatus: this.formBuilder.group({}),
      importedDate: this.formBuilder.control(importedDate),
      showAllOrdersForDate: this.formBuilder.control(
        this.viewTodaysOrdersClicked,
      ),
    });
    this.statusFilters().forEach((value, key) => {
      (this.formObject.get('orderStatus') as UntypedFormGroup).addControl(
        `${key}`,
        new UntypedFormControl(false),
      );
    });
  }

  async openCalendarModal() {
    const formValue = this.formObject.value;
    const lastSelectedDate = this.datePipe.transform(
      this?.myOrders?.filters?.filters?.importedDate,
      'yyyy-MM-dd',
    );
    const maxDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: CalendarV2ModalComponent,
        rootPageParams: {
          title: 'i18n.importOrderPage.filterByImportDate',
          multiple: false,
          timeEnabled: false,
          lastSelectedDate,
          maxDate,
          shouldDismissOnClear: false,
        },
      },
      'modal-calendar-desktop-size-lg',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal
      .onDidDismiss()
      .then(resp => this.handleCalendarModalDismiss(resp, formValue));
    await modal.present();
  }

  handleCalendarModalDismiss(resp: any, formValue: any) {
    if (resp.data?.selectedDate) {
      const importedDate = this.datePipe.transform(
        resp.data?.selectedDate,
        'M/d/yyyy',
      );
      const newFormValue = {
        ...formValue,
        importedDate,
        showAllOrdersForDate: true,
      };
      this.formObject.setValue(newFormValue);
      this.applyFilters(
        { data: { numberOfSelectedFilters: this.numberOfSelectedFilters } },
        newFormValue,
      );

      //Formats to yyyy-MM-DD
      const importDate = resp.data?.selectedDate;
      this.getImportedOrdersByDate(importDate);
    }
  }

  async openHelpModal() {
    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: HelpModalTouchComponent,
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: HelpModalDesktopComponent,
        },
        'modal-desktop-size-xl',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  showImportedOrdersActionSheet(show: boolean) {
    this.importedOrdersActionSheetIsOpen$.next(show);
  }

  showDownloadImportedOrdersModal(isOpen: boolean) {
    this.downloadImportedOrdersModalIsOpen$.next(isOpen);
  }

  handleDownloadImportedOrders(fileInfo: DownloadOrdersEvent) {
    this.store.dispatch(ORDER_ACTIONS.downloadImportedOrders(fileInfo));
  }

  openImportOrderModal(isOpen: boolean) {
    if (isOpen) {
      this.initializeImportOrderState(new Event(''));
    }
    this.importOrderModalIsOpen$.next(isOpen);
  }

  importOrderHandler(event: { file: File; fileType: string }) {
    this.store.dispatch(
      IMPORT_ORDERS_ACTIONS.load({
        ordersFile: event.file,
        fileType: event.fileType,
      }),
    );
  }

  handleImportModalDismiss(event) {
    if (this.importOrderModalIsOpen$.value) {
      this.openImportOrderModal(false);
    }
  }

  handleImportSuccess(event) {
    this.handleImportModalDismiss(event);
    setTimeout(() => {
      this.navigationHelper.routeTo(PATHS.IMPORT_ORDER);
    }, 0);
  }

  initializeImportOrderState(event) {
    this.store.dispatch(IMPORT_ORDERS_ACTIONS.initialize());
  }

  goToTop() {
    this.platformService?.platformType ===
    this.platformService.platformEnum.desktop
      ? this.virtualScroll.scrollToIndex(0)
      : this.virtualScroll.scrollToIndex(0, 'smooth');
  }

  getImportedOrdersByDate(importDate: string) {
    this.myOrdersStoreService.retrieveImportedOrdersByDate(importDate);
  }

  getImportedOrdersById(importOrderIdentifier: string) {
    this.myOrdersStoreService.retrieveImportedOrdersById(importOrderIdentifier);
  }
  toggleShowTodaysOrders(shouldShowTodaysOrders: boolean) {
    const formValue = this.formObject.value;
    const today = new Date();

    const newFormValue = {
      ...formValue,
      importedDate: shouldShowTodaysOrders
        ? this.datePipe.transform(today, 'M/d/yyyy')
        : formValue.importedDate,
      showAllOrdersForDate: shouldShowTodaysOrders,
    };
    this.formObject.setValue(newFormValue);
    this.applyFilters(
      { data: { numberOfSelectedFilters: this.numberOfSelectedFilters } },
      newFormValue,
    );
  }
  getTodaysOrders() {
    this.showImportedOrdersActionSheet(false);
    this.viewTodaysOrdersClicked = true;
    const today = new Date();
    this.toggleShowTodaysOrders(true);
    const importDate = today.toLocaleDateString('en-CA');

    this.getImportedOrdersByDate(importDate);
  }
}
